import { createFileRoute } from '@tanstack/react-router'

import type { InventoryTransferDetail } from '@/api/admin-inventory-types'
import { useCancelInventoryTransfer, useReceiveInventoryTransfer, useShipInventoryTransfer } from '@/api/api-mutations'
import { preloadGetInventoryTransfer } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { usePrompt } from '@/components/base/prompt'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { transferStatusColor } from '../-status'

export const Route = createFileRoute('/_authenticated/_general/inventory/transfers/$transferUuid')({
	component: TransferDetailRoute,
	loader: async ({ context, params }) => {
		await preloadGetInventoryTransfer(context, { path: { inventoryTransferUuid: params.transferUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.Transfer()
	}
})

function TransferDetailRoute() {
	const { transferUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => api.getInventoryTransfer({ path: { inventoryTransferUuid: transferUuid } })}
		>
			{data => <TransferDetailDrawer transfer={data} />}
		</RouteModal>
	)
}

function TransferDetailDrawer({ transfer }: { transfer: InventoryTransferDetail }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const prompt = usePrompt()
	const shipTransfer = useShipInventoryTransfer(true)
	const receiveTransfer = useReceiveInventoryTransfer(true)
	const cancelTransfer = useCancelInventoryTransfer(true)

	const busy = shipTransfer.isPending || receiveTransfer.isPending || cancelTransfer.isPending

	const handleCancel = async () => {
		const confirmed = await prompt({
			title: L.General.AreYouSure(),
			description: L.Inventory.CancelTransfer()
		})
		if (!confirmed) return
		await cancelTransfer.mutateAsync({ path: { inventoryTransferUuid: transfer.uuid } })
		toast.success(L.Inventory.CancelTransfer())
		handleSuccess()
	}

	return (
		<>
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.Transfer()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex items-center justify-between'>
						<Text className='text-sm text-ui-fg-subtle'>
							{transfer.fromInventoryLocation.name} → {transfer.toInventoryLocation.name}
						</Text>
						<StatusCell color={transferStatusColor(transfer.status)}>
							{L.Inventory.TransferStatus[transfer.status]()}
						</StatusCell>
					</div>
					<div className='flex flex-col gap-2'>
						<div className='grid grid-cols-12 gap-2 text-xs font-medium text-ui-fg-subtle'>
							<div className='col-span-9'>{L.Inventory.Column.Product()}</div>
							<div className='col-span-3 text-right'>{L.Inventory.Column.Quantity()}</div>
						</div>
						{transfer.lines.map(line => (
							<div key={line.uuid} className='grid grid-cols-12 items-center gap-2 text-sm'>
								<div className='col-span-9 min-w-0'>
									<div className='truncate'>{line.variant.productTitle}</div>
									{line.variant.sku && (
										<div className='truncate text-xs text-ui-fg-muted'>{line.variant.sku}</div>
									)}
								</div>
								<div className='col-span-3 text-right'>{line.quantity}</div>
							</div>
						))}
					</div>
				</div>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex flex-wrap items-center justify-end gap-2'>
					{(transfer.status === 'DRAFT' || transfer.status === 'IN_TRANSIT') && (
						<Button variant='danger' size='small' disabled={busy} onClick={handleCancel}>
							{L.Inventory.CancelTransfer()}
						</Button>
					)}
					{transfer.status === 'DRAFT' && (
						<Button
							size='small'
							disabled={busy}
							onClick={async () => {
								await shipTransfer.mutateAsync({ path: { inventoryTransferUuid: transfer.uuid } })
								toast.success(L.Inventory.ShipTransfer())
								handleSuccess()
							}}
						>
							{L.Inventory.ShipTransfer()}
						</Button>
					)}
					{transfer.status === 'IN_TRANSIT' && (
						<Button
							size='small'
							disabled={busy}
							onClick={async () => {
								await receiveTransfer.mutateAsync({ path: { inventoryTransferUuid: transfer.uuid } })
								toast.success(L.Inventory.ReceiveTransfer())
								handleSuccess()
							}}
						>
							{L.Inventory.ReceiveTransfer()}
						</Button>
					)}
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Close()}
						</Button>
					</Drawer.Close>
				</div>
			</Drawer.Footer>
		</>
	)
}
